import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Deep-link straight to ONE row in a list, expanded and in view.
 *
 * The approvals hub, notifications and activity log all link "the thing that needs you" to the page
 * that owns it — but a link to `/wallets` dropped the reader at the top of a list of every advance
 * ever raised, with no clue which of them they had just clicked. Finding it again was the reader's
 * problem. A `?focus=<id>` on the link makes the page open that row itself.
 *
 * Usage — spread the result into the table:
 *
 *     const focus = useFocusRow(rows);
 *     <TwDataTable expandable={{ expandedRowRender, ...focus.expandable }} rowClassName={focus.rowClassName} />
 *
 * The param is consumed once: expanding the row rewrites the URL without it, so a refresh (or a
 * Back) does not fight a row the reader has since collapsed.
 */
export function useFocusRow<T extends Record<string, any>>(
  rows: T[] | undefined,
  opts: {
    /** Query param carrying the id. Default `focus`. */
    param?: string;
    /** Row field(s) the id is matched against. Default `['id']` — add a document number where the
     *  linking side only knows that (e.g. a transfer's `transferNo`). */
    match?: string[];
    /** Called instead of expanding, for a list whose detail is a drawer/modal rather than a row. */
    onFocus?: (row: T) => void;
    /**
     * Called when the id is NOT among the rows in hand — which on a server-paged list is the normal
     * case, not an error: the linked row is simply on another page. A list whose detail view can be
     * opened by id alone (a drawer that fetches the record itself) uses this to honour the link
     * anyway. Without it the param is dropped and the reader lands at the top of a register, which is
     * exactly what deep-linking exists to avoid.
     */
    onMissing?: (wanted: string) => void;
    /** The table's own row key, when it is not `id` — a bank statement mixes payments and cash
     *  movements, so its rows are keyed `<kind>-<id>` and two rows can share an `id`. Used to expand
     *  and to scroll, so the page reaches the row the table actually drew. */
    keyOf?: (row: T) => string;
  } = {},
) {
  const { param = 'focus', match = ['id'], onFocus, onMissing, keyOf } = opts;
  const [sp, setSp] = useSearchParams();
  const wanted = sp.get(param);

  const [expandedRowKeys, setExpandedRowKeys] = useState<(string | number)[]>([]);
  const [flash, setFlash] = useState<string | null>(null);
  // Act on a given id ONCE. Without this the effect re-fires on every refetch and would re-expand a
  // row the reader has deliberately closed.
  const handled = useRef<string | null>(null);

  const target = useMemo(() => {
    if (!wanted || !rows?.length) return null;
    return rows.find((r) => match.some((f) => r[f] != null && String(r[f]) === String(wanted))) ?? null;
  }, [wanted, rows, match.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!wanted || handled.current === wanted) return;
    // Rows may still be loading — wait for them rather than giving up on the first empty render.
    if (!rows) return;
    if (!target) {
      // The row is not on this page (filtered out, or on another page of results). Hand it to
      // `onMissing` if the caller can open the record by id on its own; otherwise drop the param so
      // the URL stops promising something the page cannot deliver.
      if (rows.length) {
        handled.current = wanted;
        onMissing?.(wanted);
        const next = new URLSearchParams(sp);
        next.delete(param);
        setSp(next, { replace: true });
      }
      return;
    }
    handled.current = wanted;

    const key = keyOf ? keyOf(target) : String(target.id ?? wanted);
    if (onFocus) onFocus(target);
    else setExpandedRowKeys((cur) => (cur.includes(key) ? cur : [...cur, key]));
    setFlash(key);

    // Scroll it into view after the expansion has rendered, and let the highlight fade on its own.
    const raf = requestAnimationFrame(() => {
      document.querySelector(`[data-row-key="${CSS.escape(key)}"]`)
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
    const t = setTimeout(() => setFlash(null), 2600);

    const next = new URLSearchParams(sp);
    next.delete(param);
    setSp(next, { replace: true });

    return () => { cancelAnimationFrame(raf); clearTimeout(t); };
  }, [wanted, rows, target]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    /** Spread into `expandable` — leaves any other expandable option you pass untouched. */
    expandable: {
      expandedRowKeys,
      onExpandedRowsChange: (keys: (string | number)[]) => setExpandedRowKeys(keys),
    },
    /** Spread into `rowClassName` to tint the row you were sent to for a couple of seconds.
     *  Checks the matched field(s) as well as `id`, since a list keyed on something else (the
     *  custodian list is keyed on `repId`) carries no `id` to compare the flash against.
     *
     *  `keyOf` short-circuits that: `flash` holds the key `keyOf` produced, so a table whose key is
     *  COMPOSITE must be compared the same way. The approvals hub keys rows `<domain>:<id>`, so
     *  matching on the bare `id` field could never equal the flash and the row expanded without ever
     *  lighting up. */
    rowClassName: (r: T) => {
      if (flash == null) return '';
      const hit = keyOf
        ? keyOf(r) === flash
        : ['id', ...match].some((f) => r[f] != null && String(r[f]) === flash);
      return hit ? 'gb-row-focus' : '';
    },
    /** The row that was linked to, once it has been found. */
    focused: target,
  };
}
